/**
 * Shared "the request never reached the server" detector.
 *
 * A failed `fetch` (server unreachable, offline, DNS, connection reset, or a
 * mid-stream drop) throws a `TypeError` rather than an HTTP error; the browser
 * also exposes an explicit offline signal via `navigator.onLine`. Treat both —
 * plus a handful of message shapes various layers use — as network failures so
 * callers can show a "couldn't reach the server" message (and a retry) instead
 * of leaking an internal error (a raw Cognito identity rejection, a reCAPTCHA
 * mint failure, …) that misdescribes what actually went wrong.
 *
 * `navigator.onLine === false` is the primary discriminator: when the OS
 * reports offline, nothing could have reached the server, whatever error a
 * lower layer happened to throw. (The inverse isn't reliable — `onLine` can be
 * `true` with dead connectivity — so we also match thrown TypeErrors and the
 * usual fetch-failure message strings.)
 */
export function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true;
  }
  if (err instanceof TypeError) {
    return true;
  }
  if (err instanceof Error) {
    return /networkerror|failed to fetch|load failed|network ?request failed/i.test(
      err.message,
    );
  }
  return false;
}
